const isTranslation = (value) => Boolean(value && typeof value === "object"
  && typeof value.source_hash === "string" && value.source_hash
  && typeof value.model === "string" && value.model
  && typeof value.stem === "string"
  && Array.isArray(value.options) && Array.isArray(value.shared_context));

const documentQuestionId = (question) => String(question?.math_bank?.id || question?.id || "");

export const isBilingualExamTemplate = (templateId) => [
  "bilingual_exam_paper", "bilingual_chinese_first_exam_paper",
].includes(templateId);

export const withBilingualTemplate = (document, templateId) => document ? {
  ...document,
  metadata: {
    ...document.metadata,
    template_id: templateId,
    bilingual_order: templateId === "bilingual_chinese_first_exam_paper" ? "chinese_first" : "english_first",
  },
} : null;

export const selectBilingualTranslations = (ids, ...caches) => {
  const translations = Object.assign({}, ...caches.map((cache) => Object.fromEntries(
    Object.entries(cache || {}).filter(([, value]) => isTranslation(value)),
  )));
  return Object.fromEntries(ids.map(String)
    .filter((id) => isTranslation(translations[id]))
    .map((id) => [id, translations[id]]));
};

export const restoreRecordBilingualCache = (record) => {
  const payload = record.payload || {};
  const items = record.items || [];
  const ids = items.map((item) => String(item.snapshot?.id || item.question_uuid || ""));
  const sourceDocument = payload.document;
  const documentSections = Array.isArray(sourceDocument?.sections) ? sourceDocument.sections : [];
  const documentQuestions = documentSections.flatMap((section) => section.questions || []);
  const snapshotTranslations = Object.fromEntries(items
    .filter((item) => isTranslation(item.snapshot?.bilingual))
    .map((item) => [String(item.snapshot.id || item.question_uuid), item.snapshot.bilingual]));
  const documentTranslations = Object.fromEntries(documentQuestions
    .filter((question) => isTranslation(question.bilingual))
    .map((question) => [documentQuestionId(question), question.bilingual]));
  const translations = selectBilingualTranslations(ids, snapshotTranslations, documentTranslations, payload.bilingualTranslations);
  const documentIds = documentQuestions.map(documentQuestionId);
  const complete = ids.length > 0 && documentQuestions.length === ids.length
    && JSON.stringify([...ids].sort()) === JSON.stringify([...documentIds].sort())
    && documentQuestions.every((question) => isTranslation(translations[documentQuestionId(question)]));
  // The backend validates saved English against this document's source hash.
  // Restoring its complete snapshot needs neither a new translation job nor a
  // browser-side reconstruction of the bank's formulas and shared conditions.
  const document = complete ? {
    ...sourceDocument,
    sections: sourceDocument.sections.map((section) => ({
      ...section,
      questions: (section.questions || []).map((question) => ({
        ...question, bilingual: translations[documentQuestionId(question)],
      })),
    })),
  } : null;
  return { translations, document: isBilingualExamTemplate(record.template_id)
    ? withBilingualTemplate(document, record.template_id) : document };
};
